import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Drawer,
  Button,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Plus } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import CustomChartsPicker from "./CustomChartsPicker";
import FilterRow from "./FilterRow";
import ChartPreview from "./ChartPreview";

const formatFieldName = (field) =>
  field.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export const COLOR_PALETTE = [
  "hsl(155 72% 44%)",
  "hsl(38 95% 50%)",
  "hsl(217 91% 52%)",
  "hsl(0 72% 50%)",
  "hsl(175 60% 45%)",
  "hsl(271 68% 55%)",
  "hsl(152 58% 57%)",
  "hsl(155 77% 34%)",
  "hsl(219 99% 62%)",
  "hsl(217 90% 43%)",
  "hsl(42 93% 60%)",
  "hsl(27 93% 55%)",
  "hsl(340 75% 60%)",
  "hsl(192 72% 50%)",
  "hsl(74 72% 50%)",
];

export const inputStyle = {
  width: "100%",
  height: 36,
  borderRadius: "calc(-2px + 0.75rem)",
  border: "1px solid hsl(140 8% 88%)",
  backgroundColor: "hsl(140 10% 95%)",
  px: 1.25,
  fontSize: 13,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  "&:focus": { outline: "none", borderColor: "hsl(140 8% 82%)" },
};

export const selectStyle = {
  height: 36,
  borderRadius: "calc(-2px + 0.75rem)",
  fontSize: 13,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  backgroundColor: "hsl(140 10% 95%)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 82%)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 82%)", borderWidth: 1 },
};

const ALLOWED_MODELS = ["Candidate", "Recruiter", "JobApplication", "JobOffer", "Credit", "EmailDelivery"];

const ChartBuilder = ({ open, chart, onClose, onSave, statsPageId, charts = [] }) => {
  const [title, setTitle] = useState("");
  const [chartModel, setChartModel] = useState(ALLOWED_MODELS[0]);
  const [chartType, setChartType] = useState("kpi");
  const [groupBy, setGroupBy] = useState("");
  const [timeBucket, setTimeBucket] = useState("");
  const [metric, setMetric] = useState("id");
  const [func, setFunc] = useState("count");
  const [filters, setFilters] = useState([]);
  const [fields, setFields] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedCustomChart, setSelectedCustomChart] = useState(null);
  const [chartMode, setChartMode] = useState("saved");
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open) return;

    let resolvedModel;

    if (chart) {
      setTitle(chart.title);
      const customKey = chart.display_config?.custom_chart_key;
      if (customKey) {
        setSelectedCustomChart({ key: customKey });
        setChartMode("custom");
        setChartModel("");
        resolvedModel = "";
      } else {
        setSelectedCustomChart(null);
        setChartMode("saved");
        resolvedModel = chart.chart_model || ALLOWED_MODELS[0];
        setChartModel(resolvedModel);
      }
      setChartType(chart.chart_type);
      setGroupBy(chart.aggregation?.group_by || "");
      setTimeBucket(chart.aggregation?.time_bucket || "");
      setMetric(chart.aggregation?.metric || "id");
      setFunc(chart.aggregation?.function || "count");
      setFilters(Array.isArray(chart.filters) ? chart.filters : []);
    } else {
      setTitle("");
      setSelectedCustomChart(null);
      setChartMode("saved");
      resolvedModel = ALLOWED_MODELS[0];
      setChartModel(resolvedModel);
      setChartType("kpi");
      setGroupBy("");
      setTimeBucket("");
      setMetric("id");
      setFunc("count");
      setFilters([]);
    }

    if (resolvedModel) {
      sh.get(`/internal/saved_charts/filter_options?chart_model=${resolvedModel}`)
        .then((res) => setFields(res.data.fields || []))
        .catch(() => setFields([]));
    } else {
      setFields([]);
    }
  }, [chart, open]);

  // Re-fetch fields when the user manually changes the model via the dropdown.
  const prevModelRef = useRef(null);
  useEffect(() => {
    if (!open || !chartModel) return;
    if (prevModelRef.current === chartModel) return;
    prevModelRef.current = chartModel;
    sh.get(`/internal/saved_charts/filter_options?chart_model=${chartModel}`)
      .then((res) => setFields(res.data.fields || []))
      .catch(() => setFields([]));
  }, [open, chartModel]);

  const cleanFilters = useMemo(
    () =>
      filters.filter((f) => {
        if (!f.field || !f.operator) return false;
        if (f.value === null || f.value === undefined || f.value === "") return false;
        if (Array.isArray(f.value) && f.value.every((v) => v === "" || v == null)) return false;
        if (f.operator === "between" && (!Array.isArray(f.value) || f.value.some((v) => v === "" || v == null))) return false;
        return true;
      }),
    [filters]
  );

  const handleCustomChartSelect = (customChart) => {
    setSelectedCustomChart(customChart);
    setTitle(customChart.name);
    setChartModel("");
    setChartType(customChart.chart_type);
    setGroupBy("");
    setTimeBucket("");
    setMetric("id");
    setFunc("count");
    setFilters([]);
  };

  useEffect(() => {
    if (!open) return;
    if (!selectedCustomChart) return;

    setLoadingPreview(true);
    const handle = setTimeout(() => {
      sh.post("/internal/saved_charts/custom_preview", {
        custom_chart_key: selectedCustomChart.key,
        filters: cleanFilters,
      })
        .then((res) => setPreviewData(res.data))
        .catch(() => setPreviewData(null))
        .finally(() => setLoadingPreview(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, selectedCustomChart, cleanFilters]);

  useEffect(() => {
    if (!open || selectedCustomChart) return;
    if (chartType !== "kpi" && !groupBy) {
      setPreviewData(null);
      return;
    }

    setLoadingPreview(true);
    const handle = setTimeout(() => {
      sh.post("/internal/saved_charts/preview", {
        chart_model: chartModel,
        title: title || "Preview",
        chart_type: chartType,
        filters: cleanFilters,
        aggregation: { group_by: chartType === "kpi" ? null : groupBy, metric, function: func, time_bucket: timeBucket || null },
      })
        .then((res) => setPreviewData(res.data))
        .catch(() => setPreviewData(null))
        .finally(() => setLoadingPreview(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, chartModel, groupBy, chartType, func, metric, title, cleanFilters, timeBucket, selectedCustomChart]);

  const handleSave = () => {
    if (!title.trim()) return enqueueSnackbar("Title is required", { variant: "error" });
    if (chartMode === "custom" && !selectedCustomChart) return enqueueSnackbar("Please select a custom chart", { variant: "error" });
    if (chartMode === "saved" && chartType !== "kpi" && !groupBy) return enqueueSnackbar("Please select a field to group by", { variant: "error" });

    const colors = Array.from({ length: 15 }, () =>
      COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)]
    );

    const defaultH = chartType === "kpi" ? 1 : 3;
    const defaultW = chartType === "kpi" ? 3 : 6;
    const nextY = charts.reduce((max, c) => {
      const cy = (c.display_config?.layout?.y ?? 0) + (c.display_config?.layout?.h ?? 3);
      return cy > max ? cy : max;
    }, 0);

    const payload = {
      saved_chart: {
        chart_model: selectedCustomChart ? "" : chartModel,
        title,
        chart_type: chartType,
        filters: cleanFilters,
        aggregation: { group_by: chartType === "kpi" ? null : groupBy, metric, function: func, time_bucket: timeBucket || null },
        display_config: selectedCustomChart
          ? { custom_chart_key: selectedCustomChart.key, colors, ...(!chart ? { layout: { x: 0, y: nextY, w: chartType === "kpi" ? 3 : 6, h: chartType === "kpi" ? 1 : 3 } } : {}) }
          : { colors, ...(!chart ? { layout: { x: 0, y: nextY, w: defaultW, h: defaultH } } : {}) },
        stats_page_id: statsPageId,
      },
    };

    const request = chart
      ? sh.put(`/internal/saved_charts/${chart.id}`, payload)
      : sh.post("/internal/saved_charts", payload);

    request
      .then((res) => {
        enqueueSnackbar("Chart saved", { variant: "success" });
        onSave(res.data);
      })
      .catch(() => enqueueSnackbar("Failed to save chart", { variant: "error" }));
  };

  const addFilter = () => setFilters((curr) => [...curr, { field: "", operator: "", value: "" }]);
  const updateFilter = (i, next) => setFilters((curr) => curr.map((f, idx) => (idx === i ? next : f)));
  const removeFilter = (i) => setFilters((curr) => curr.filter((_, idx) => idx !== i));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%",
          borderLeft: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid hsl(140 8% 88%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 16, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            {chart ? "Edit Chart" : "New Chart"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2, "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { background: "hsl(140 8% 82%)", borderRadius: 3 } }}>
          <FormControlLabel
            control={
              <Switch
                checked={chartMode === "custom"}
                onChange={(e) => {
                  if (e.target.checked) {
                    setChartMode("custom");
                    setChartModel("");
                    setGroupBy("");
                    setFilters([]);
                    setPreviewData(null);
                  } else {
                    setChartMode("saved");
                    setSelectedCustomChart(null);
                    setChartModel(ALLOWED_MODELS[0]);
                    setPreviewData(null);
                  }
                }}
                sx={{ "& .MuiSwitch-switchBase:hover": { backgroundColor: "transparent" } }}
              />
            }
            label={chartMode === "custom" ? "Custom Chart" : "Saved Chart"}
            sx={{ "& .MuiFormControlLabel-label": { fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13 } }}
          />

          <Box>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
              Title
            </Typography>
            <Box
              component="input"
              placeholder="Chart title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={inputStyle}
            />
          </Box>

          {chartMode === "custom" && (
            <CustomChartsPicker onSelect={handleCustomChartSelect} selectedKey={selectedCustomChart?.key} />
          )}

          {chartMode === "saved" && (
            <FormControl fullWidth>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
              Model
              </Typography>
              <Select value={chartModel} onChange={(e) => { setChartModel(e.target.value); setGroupBy(""); setFilters([]); }} sx={selectStyle}>
                {ALLOWED_MODELS.map((m) => (
                  <MenuItem key={m} value={m}>{m.replace(/([A-Z])/g, " $1").trim()}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {chartMode === "saved" && (
            <FormControl fullWidth>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
              Chart Type
              </Typography>
              <Select value={chartType} onChange={(e) => setChartType(e.target.value)} sx={selectStyle}>
                <MenuItem value="kpi">KPI Card</MenuItem>
                <MenuItem value="bar">Bar</MenuItem>
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="pie">Pie</MenuItem>
                <MenuItem value="doughnut">Doughnut</MenuItem>
              </Select>
            </FormControl>
          )}

          {chartMode === "saved" && chartType !== "kpi" && (
            <FormControl fullWidth>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
                Group By Field
              </Typography>
              <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} sx={selectStyle}>
                {fields.map((f) => (
                  <MenuItem key={f.name} value={f.name}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {chartMode === "saved" && chartType !== "kpi" && groupBy && fields.find(f => f.name === groupBy)?.type === "datetime" && (
            <FormControl fullWidth>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
                Time Bucket
              </Typography>
              <Select value={timeBucket} onChange={(e) => setTimeBucket(e.target.value)} sx={selectStyle}>
                <MenuItem value="">None</MenuItem>
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="quarter">Quarter</MenuItem>
                <MenuItem value="year">Year</MenuItem>
              </Select>
            </FormControl>
          )}

          {chartMode === "saved" && (
            <FormControl fullWidth>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
              Function
              </Typography>
              <Select value={func} onChange={(e) => setFunc(e.target.value)} sx={selectStyle}>
                <MenuItem value="count">Count</MenuItem>
                <MenuItem value="sum">Sum</MenuItem>
                <MenuItem value="average">Average</MenuItem>
              </Select>
            </FormControl>
          )}

          {chartMode === "saved" && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                Filters
                </Typography>
                <Button
                  size="small"
                  disableRipple
                  onClick={addFilter}
                  startIcon={<Plus size={14} />}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    color: "hsl(150 38% 22%)",
                    "& .MuiButton-startIcon": { mr: 0.5 },
                    transition: "opacity 0.15s ease",
                    "&:hover": { backgroundColor: "hsl(140 8% 90%)", opacity: 0.9 },
                  }}
                >
                Add filter
                </Button>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {filters.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: "hsl(150 10% 60%)", fontStyle: "italic" }}>
                  No filters — chart shows all records.
                  </Typography>
                ) : (
                  filters.map((f, i) => (
                    <FilterRow
                      key={i}
                      filter={f}
                      fields={fields}
                      onChange={(next) => updateFilter(i, next)}
                      onRemove={() => removeFilter(i)}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}

          {(chartMode === "saved" || selectedCustomChart) && (
            <ChartPreview
              chartType={chartType}
              previewData={previewData}
              loadingPreview={loadingPreview}
              title={title}
            />
          )}
        </Box>

        <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid hsl(140 8% 88%)", display: "flex", gap: 1.5 }}>
          <Button
            size="small"
            disableRipple
            disableElevation
            onClick={onClose}
            sx={{
              flex: 1,
              height: 40,
              px: 1.5,
              borderRadius: "calc(-2px + 0.75rem)",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 13,
              color: "hsl(150 10% 46%)",
              "&:hover": { backgroundColor: "hsl(140 10% 95%)", color: "hsl(150 30% 12%)" },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            disableRipple
            disableElevation
            onClick={handleSave}
            variant="contained"
            sx={{
              flex: 1,
              height: 40,
              px: 1.5,
              borderRadius: "calc(-2px + 0.75rem)",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 13,
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              transition: "opacity 0.15s ease",
              "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
              "&:disabled": { backgroundColor: "hsla(150, 38%, 17%, 0.4)", color: "hsla(80, 97%, 72%, 0.6)" },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export { formatFieldName };
export default ChartBuilder;
